<template>
  <div class="worker-dashboard">
    <!-- Header -->
    <dashboard-context-header title="MON SERVICE" @context-change="onContextChange" />

    <!-- Toggle pour voir le plan d'action -->
    <div class="action-toggle">
      <label class="toggle-label">
        <input type="checkbox" v-model="showActionPlan" class="toggle-input" />
        <span class="toggle-slider"></span>
        <span class="toggle-text">Voir mon plan d'action</span>
      </label>
    </div>

    <!-- Plan d'action (si activé) -->
    <div v-if="showActionPlan" class="action-plan-section">
      <h2 class="section-header">Actions du jour</h2>

      <div class="pagination">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">‹</button>
        <span class="pagination-number">{{ currentPage }}</span>
        <button class="pagination-btn" :disabled="currentPage === actions.length" @click="currentPage++">›</button>
      </div>

      <div class="action-cards-wrapper">
        <div v-for="(action, idx) in actions" :key="action.id" v-show="idx + 1 === currentPage" class="action-card">
          <div class="action-card-header">
            <h3 class="action-title">{{ action.title }}</h3>
            <span class="action-restaurant">{{ action.restaurant }}</span>
          </div>

          <div class="action-details">
            <div class="action-detail-item">
              <span class="detail-label">Durée :</span>
              <span class="detail-value">{{ action.duration }}</span>
            </div>
            <div class="action-detail-item">
              <span class="detail-label">Description :</span>
              <span class="detail-value">{{ action.description }}</span>
            </div>
          </div>

          <button @click="viewInstructions(action)" class="btn-link">Voir les instructions →</button>

          <button v-if="action.status === 'pending'" @click="startAction(action)" class="btn btn-success">
            Démarrer l'action
          </button>
          <button v-else-if="action.status === 'started'" class="btn btn-in-progress" disabled>Action en cours</button>
          <button v-else class="btn btn-completed" disabled>✓ Terminé</button>
        </div>
      </div>

      <div class="add-action-card">
        <div class="add-action-icon">+</div>
        <div class="add-action-text">Lancer une nouvelle action</div>
      </div>
    </div>

    <!-- Cartes de service principales -->
    <div v-else class="cards-grid">
      <!-- Aiguillage §3 (meal-feedback-restitution) : selon `show_detailed_feedback` de la
           cantine, on propose SOIT la saisie du retour détaillé par plat (SupervisorFeedback),
           SOIT « Mon avis sur le repas du jour » (MealFeedback) — jamais les deux à la fois.
           La cantine, le service et la date viennent du contexte du dashboard. -->
      <supervisor-feedback-panel
        v-if="featureDetailedFeedback"
        :canteen-id="canteenId"
        :service-id="selectedService"
        :date="selectedDate"
        @saved="reloadSupervisorStats"
      />
      <meal-feedback-panel
        v-else-if="featureMealFeedback"
        :canteen-id="canteenId"
        :service-id="selectedService"
        :date="selectedDate"
        @saved="reloadMealStats"
      />

      <!-- Restitution de l'agrégat de satisfaction repas (deux moyennes + écart).
           Rechargée sur l'évènement `saved` du formulaire, pour que la note apparaisse
           sans attendre un rechargement complet de la page. -->
      <meal-feedback-stats-panel ref="mealFeedbackStats" :canteen-id="canteenId" />
      <supervisor-feedback-stats-panel ref="supervisorFeedbackStats" :canteen-id="canteenId" />

      <!-- Card 1: Menu du jour -->
      <dashboard-card
        title="📋 Menu du jour"
        description="Consulter le menu et les informations du service"
        badge="Aujourd'hui"
        link-label="Voir le menu"
        @click="showMenuDetails = true"
      />

      <!-- Card 2: Effectifs -->
      <dashboard-card
        v-if="featureEffectifs"
        title="👥 Effectifs"
        description="Gérer les effectifs prévus et présents"
        badge="Aujourd'hui"
        link-label="Modifier"
        @click="showEffectifs = true"
      />

      <!-- Card 3: Communications -->
      <dashboard-card
        title="📢 Communications"
        description="Absences, alertes et informations importantes"
        :badge="unreadMessagesBadge"
        badge-variant="orange"
        link-label="Voir"
        @click="showCommunications = true"
      />

      <!-- Card 4: Surplus -->
      <dashboard-card
        v-if="featureSurplus"
        title="📦 Signaler un surplus"
        description="Déclarer les excédents alimentaires du service"
        link-label="Déclarer"
        @click="showSurplusForm = true"
      />

      <!-- Card 5: Gaspillage (fin de service) -->
      <dashboard-card
        v-if="featureWaste"
        title="♻️ Mesurer le gaspillage"
        description="Pesée des restes pour sensibiliser les enfants"
        badge="Fin de service"
        badge-variant="orange"
        link-label="Commencer"
        variant="highlight"
        @click="showWasteInput = true"
      />
    </div>

    <!-- Modal: Menu du jour -->
    <div v-if="showMenuDetails" class="modal-overlay" @click="showMenuDetails = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMenuDetails = false">✕</button>
        <h2 class="modal-title">📋 Menu du jour</h2>
        <menu-du-jour-panel :canteen-id="canteenId" :date="selectedDate" />
      </div>
    </div>

    <!-- Modal: Effectifs -->
    <div v-if="showEffectifs && featureEffectifs" class="modal-overlay" @click="showEffectifs = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showEffectifs = false">✕</button>
        <h2 class="modal-title">👥 Effectifs</h2>
        <effectifs-panel
          :canteen-id="canteenId"
          :service-id="selectedService"
          :date="selectedDate"
          :readonly="false"
          :variant="effectifVariant"
        />
      </div>
    </div>

    <!-- Modal: Communications -->
    <div v-if="showCommunications" class="modal-overlay" @click="showCommunications = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showCommunications = false">✕</button>
        <communication-panel2 :canteen-id="canteenId" :date="selectedDate" :service-id="selectedService" />
      </div>
    </div>

    <!-- Modal: Surplus Form -->
    <div v-if="showSurplusForm" class="modal-overlay" @click="showSurplusForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSurplusForm = false">✕</button>
        <surplus-form
          :canteen-id="canteenId"
          :service-id="selectedService"
          :meal-date="selectedDate"
          @close="showSurplusForm = false"
        />
      </div>
    </div>

    <!-- Modal: Gaspillage -->
    <div v-if="showWasteInput && featureWaste" class="modal-overlay" @click="showWasteInput = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showWasteInput = false">✕</button>
        <waste-input-panel
          :canteen-id="canteenId"
          :variant="wasteVariant"
          :meal-date="selectedDate"
          :service-id="selectedService"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SurplusForm from "./SurplusForm.vue"
import CommunicationPanel2 from "@/components/CommunicationPanel2.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import EffectifsPanel from "@/components/EffectifsPanel.vue"
import MenuDuJourPanel from "@/components/MenuDuJourPanel.vue"
import MealFeedbackPanel from "@/components/MealFeedbackPanel.vue"
import MealFeedbackStatsPanel from "@/components/MealFeedbackStatsPanel.vue"
import SupervisorFeedbackStatsPanel from "@/components/SupervisorFeedbackStatsPanel.vue"
import SupervisorFeedbackPanel from "@/components/SupervisorFeedbackPanel.vue"
import DashboardCard from "@/components/DashboardCard.vue"

export default {
  name: "WorkerDashboard",
  components: {
    SurplusForm,
    CommunicationPanel2,
    WasteInputPanel,
    DashboardContextHeader,
    EffectifsPanel,
    MenuDuJourPanel,
    MealFeedbackPanel,
    MealFeedbackStatsPanel,
    SupervisorFeedbackStatsPanel,
    SupervisorFeedbackPanel,
    DashboardCard,
  },
  data() {
    return {
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      showActionPlan: false,
      currentPage: 1,
      actions: [
        {
          id: 1,
          title: "action 1",
          restaurant: "mon restau",
          duration: "1 jours",
          description: "Vérifier les allergies avant le service",
          status: "pending",
        },
        {
          id: 2,
          title: "action 2",
          restaurant: "mon restau",
          duration: "1 jours",
          description: "Compter les portions restantes",
          status: "started",
        },
        {
          id: 3,
          title: "Semaine du goût",
          restaurant: "mon restau",
          duration: "0 jours",
          description: "Animation spéciale autour des fruits locaux",
          status: "completed",
        },
      ],
      showMenuDetails: false,
      showEffectifs: false,
      showCommunications: false,
      showSurplusForm: false,
      showWasteInput: false,
      featureEffectifs: true,
      featureWaste: true,
      featureSurplus: true,
      featureDetailedFeedback: true,
      featureMealFeedback: true,
      unreadMessages: 0,
      effectifVariant: "SIMPLE",
      wasteVariant: "barquette",
    }
  },
  computed: {
    // Le composant n'affiche rien pour une chaîne vide : l'absence de badge se dit ici,
    // elle ne se code plus par un `v-if` sur le `<span>`.
    unreadMessagesBadge() {
      return this.unreadMessages > 0 ? String(this.unreadMessages) : ""
    },
  },
  methods: {
    // Recharge l'agrégat après une saisie, pour restituer la valeur sans reload de page.
    reloadSupervisorStats() {
      if (this.$refs.supervisorFeedbackStats) this.$refs.supervisorFeedbackStats.load()
    },
    reloadMealStats() {
      if (this.$refs.mealFeedbackStats) this.$refs.mealFeedbackStats.load()
    },
    onContextChange({
      canteenId,
      selectedDate,
      selectedService,
      effectifVariant,
      wasteVariant,
      featureEffectifs,
      featureWaste,
      featureSurplus,
      featureDetailedFeedback,
      featureMealFeedback,
    }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.effectifVariant = effectifVariant || "SIMPLE"
      this.wasteVariant = wasteVariant || "barquette"
      this.featureEffectifs = featureEffectifs ?? true
      this.featureWaste = featureWaste ?? true
      this.featureSurplus = featureSurplus ?? true
      this.featureDetailedFeedback = featureDetailedFeedback ?? true
      this.featureMealFeedback = featureMealFeedback ?? true
    },
    viewInstructions(action) {
      alert(`Instructions pour: ${action.title}`)
    },
    startAction(action) {
      action.status = "started"
      alert(`Action "${action.title}" démarrée`)
    },
  },
}
</script>

<style scoped>
/* Réutilisation des styles du ManagerDashboard */
.worker-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

/* Toggle */
.action-toggle {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  width: 48px;
  height: 26px;
  background: #d1d5db;
  border-radius: 9999px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: "";
  position: absolute;
  width: 20px;
  height: 20px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle-input:checked + .toggle-slider {
  background: #2563eb;
}

.toggle-input:checked + .toggle-slider::before {
  transform: translateX(22px);
}

.toggle-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1a1a1a;
}

/* Plan d'action */
.action-plan-section {
  margin-bottom: 2rem;
}

.section-header {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.pagination-btn {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #1a1a1a;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #2563eb;
}

.pagination-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.pagination-number {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: white;
  font-weight: 600;
  border-radius: 8px;
}

.action-cards-wrapper {
  position: relative;
  min-height: 280px;
  margin-bottom: 1.5rem;
}

.action-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  max-width: 600px;
  margin: 0 auto;
}

.action-card-header {
  margin-bottom: 1.25rem;
}

.action-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.action-restaurant {
  font-size: 0.875rem;
  color: #6b7280;
}

.action-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.action-detail-item {
  display: flex;
  gap: 0.5rem;
}

.detail-label {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 0.875rem;
}

.detail-value {
  color: #6b7280;
  font-size: 0.875rem;
}

.btn-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: color 0.2s;
}

.btn-link:hover {
  color: #1d4ed8;
}

.add-action-card {
  max-width: 600px;
  margin: 0 auto;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
}

.add-action-card:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.add-action-icon {
  font-size: 3rem;
  color: #2563eb;
  margin-bottom: 0.5rem;
}

.add-action-text {
  font-weight: 500;
  color: #2563eb;
}

/* Cartes de service */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Sélecteur d'ambiance */
.ambiance-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
}

.ambiance-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.ambiance-btn:hover {
  background: #f3f4f6;
}

.ambiance-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
}

.ambiance-emoji {
  font-size: 2rem;
}

.ambiance-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

/* Modal (styles identiques au ManagerDashboard) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #1a1a1a;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Sections de détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.input-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.input-field {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.input-field:focus {
  outline: none;
  border-color: #2563eb;
}

/* Stats row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Menu list */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-success {
  background: #22c55e;
  color: white;
}

.btn-success:hover {
  background: #16a34a;
}

.btn-in-progress {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.btn-completed {
  background: #d1fae5;
  color: #065f46;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 640px) {
  .worker-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .modal-content {
    padding: 1.5rem;
    border-radius: 12px;
  }
}
</style>
